import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import styled from 'styled-components'

import { Community, Post } from '@sportsyou/api'
import { Post as PostComponent, Skeleton, Table } from '@sportsyou/react-dom-ui'

import usePagesPortal from '../UsePagesPortal'
import { Colors } from '@sportsyou/core'
import { StatusCircle } from '../../../styles/global-styles'

interface Props {
  page: Community
}

const PostThumbnail = ({
  onClick,
  page,
  post,
}: {
  onClick: (post: Post) => void
  page: Community
  post: Post
}) => {
  const handleOnClick = useCallback(() => {
    onClick(post)
  }, [onClick, post])

  return (
    <PostThumbContainer onClick={handleOnClick}>
      <PostThumbCover />
      <PostThumbWrapper>
        <PostComponent
          {...post}
          community={page}
          hideApprovalUI
          hideFollowButton
        />
      </PostThumbWrapper>
    </PostThumbContainer>
  )
}

const tableCustomStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
      fontSize: '16px',
    },
  },
}

export default function PagesContentPortalProfilePostList(props: Props) {
  const navigate = useNavigate()

  const { page, fetchPosts, isLoading, posts } = usePagesPortal({
    communityId: props.page.id as string,
  })

  const onRowClicked = useCallback(
    (post: Post) => {
      navigate(`/pages/${page.id}/posts/${post.id}`)
    },
    [page.id, navigate]
  )

  const dataColumns = useMemo(() => {
    function isScheduledAfterNow(post: Post) {
      if (!post.scheduledTime) return false
      return moment(post.scheduledTime).isAfter(moment())
    }

    function postStatusString(post: Post) {
      if (post.deletedAt) {
        return 'Deleted'
      } else if (post.scheduledTime && isScheduledAfterNow(post)) {
        return 'Scheduled'
      } else if (post.approvedAt) {
        return 'Active'
      } else {
        return 'Pending approval'
      }
    }

    function postStatusIndicatorData(post: Post) {
      const label = postStatusString(post)
      let color = Colors.BLACK
      switch (label) {
        case 'Scheduled':
          color = Colors.HAVELOCK_BLUE
          break
        case 'Active':
          color = Colors.MOUNTAIN_MEADOW
          break
        case 'Pending approval':
          color = Colors.GOLD
          break
        case 'Deleted':
          color = Colors.PUNCH
          break
      }
      return { color, label }
    }

    const statusSort = (rowA: Post, rowB: Post) => {
      const a = postStatusString(rowA) || ''
      const b = postStatusString(rowB) || ''
      return a.localeCompare(b)
    }

    return [
      {
        format: (post: Post) => (
          <PostThumbnail page={page} post={post} onClick={onRowClicked} />
        ),
        name: 'Post',
        reorder: true,
        selector: (post: Post) =>
          moment(post.createdAt).format('MM/DD/YYYY hh:mm A'),
        sortable: true,
        style: {
          cursor: 'pointer',
          height: '120px',
        },
        width: '200px',
      },
      {
        name: 'Posted Date',
        reorder: true,
        selector: (post: Post) =>
          moment(post.createdAt).format('MM/DD/YYYY hh:mm A'),
        sortable: true,
        width: '200px',
      },
      {
        name: 'Scheduled Date',
        reorder: true,
        selector: (post: Post) => {
          if (!post.scheduledTime) return '-'
          return moment(post.scheduledTime).format('MM/DD/YYYY hh:mm A')
        },
        sortable: true,
        width: '200px',
      },
      {
        name: 'Title',
        reorder: true,
        selector: (post: Post) => {
          let message = post.communityPostInfo?.postTitle
          if (!message) {
            if ((post.message?.length ?? 0) > 80) {
              message = `${post.message?.slice(0, 80)}...`
            } else {
              message = post.message
            }
          }
          return message
        },
        sortable: true,
        style: {
          cursor: 'pointer',
          fontWeight: 'bold',
        },
      },
      {
        name: 'Status',
        selector: (row: Post) => !!row.isPending,
        sortable: true,
        sortFunction: statusSort,
        reorder: true,
        maxWidth: '150px',
        cell: (post: Post) => {
          const status = postStatusIndicatorData(post)
          return (
            <>
              <StatusCircle color={status.color} />
              {status.label}
            </>
          )
        },
      },
    ]
  }, [onRowClicked, page])

  useEffect(() => {
    if (!page.id) return
    fetchPosts({
      communityId: page.id,
      perPage: 100,
    })
  }, [fetchPosts, page.id])

  return (
    <Table
      // selectableRows
      columns={dataColumns as any}
      customStyles={tableCustomStyles}
      data={posts}
      defaultSortAsc={true}
      highlightOnHover
      onRowClicked={onRowClicked}
      progressPending={isLoading}
      striped
      progressComponent={
        <Skeleton
          barGap={8}
          barHeight={120}
          showBars
          showHeader={false}
          width={'100%'}
        />
      }
    />
  )
}

const PostThumbWrapper = styled.div`
  border: 6px solid #ccc;
  height: 250px;
  left: 0;
  top: 0;
  transform: scale(0.5) translate(-50%, -30%);
  transition: all 0.2s ease-in-out;
  width: 300px;
`

const PostThumbCover = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: 1;
`

const PostThumbContainer = styled.div`
  height: 120px;
  overflow: hidden;
  position: relative;
  width: 200px;

  &:hover ${PostThumbWrapper} {
    transform: scale(0.5) translate(-50%, -40%) rotate(-1deg);
  }
`
